import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { lazy, Suspense, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { AlertTriangle, Calendar, CheckCircle2, ChevronDown, Clock, CreditCard, Loader2, MapPin, Receipt, ShieldCheck, Users } from 'lucide-react'
import { ConnectError, Code } from '@connectrpc/connect'
import { usePublicBooking } from '@/stores/booking.store'
import { useCreateCheckoutSession, useCreateResidualTipCheckoutSession, usePublicInvoice } from '@/stores/invoice.store'
import { setToken } from '@/stores/token.store'
import { getErrorDescription } from '@/lib/error-utils'
import { savePaymentSessionContext } from '@/lib/payment-session-context'
import { captureError, trackPaymentEvent } from '@/lib/sentry'
import { timestampToDate, formatDateOnlyUTC, formatDateTimeRange } from '@/lib/date-utils'
import { formatDiscountLabel } from '@/lib/discount-label'
import { cn, formatCents } from '@/lib/utils'
import { getCustomerFacingInvoicePaymentTotals } from '@/lib/invoice-payment-totals'
import { questionnaireGatesPayment } from '@/lib/questionnaire-gate'
import { PublicInvoice } from '@/gen/cortado/v1/inquiries_pb'
import { ErrorBoundary } from '@/components/error-boundary'
import { SessionCountdown } from '@/components/session-countdown'
import { PortalHeader } from '@/components/portal-header'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { PortalFooter } from '@/components/portal-footer'

const EmbeddedCheckoutForm = lazy(() =>
  import('@/components/embedded-checkout').then(m => ({ default: m.EmbeddedCheckoutForm }))
)

type CheckoutSearchParams = {
  token?: string
  invoice_id?: string
  schedule_id?: string
  tip_only?: boolean
}

export const Route = createFileRoute('/booking/$id/checkout')({
  component: BookingCheckoutPage,
  validateSearch: (search: Record<string, unknown>): CheckoutSearchParams => ({
    token: typeof search.token === 'string' ? search.token : undefined,
    invoice_id: typeof search.invoice_id === 'string' ? search.invoice_id : undefined,
    schedule_id: typeof search.schedule_id === 'string' ? search.schedule_id : undefined,
    tip_only: search.tip_only === true || search.tip_only === 'true' ? true : undefined,
  }),
})

function BookingCheckoutPage() {
  const { id } = useParams({ from: '/booking/$id/checkout' })
  const navigate = useNavigate()
  const { token, invoice_id: invoiceId, schedule_id: scheduleId, tip_only: tipOnly } = useSearch({ from: '/booking/$id/checkout' })
  const { booking, isLoading: isBookingLoading, hasLoaded: hasBookingLoaded, isError: isBookingError } = usePublicBooking(id)
  const { invoice, isLoading: isInvoiceLoading, isError: isInvoiceError } = usePublicInvoice(invoiceId ?? '')
  const { createCheckoutSession } = useCreateCheckoutSession()
  const { createResidualTipCheckoutSession } = useCreateResidualTipCheckoutSession()
  const [checkout, setCheckout] = useState<{
    sessionId: string
    clientSecret: string
    stripeAccountId: string
    expiresAt: Date | null
    amountCents: bigint
  } | null>(null)
  const [isCreatingSession, setIsCreatingSession] = useState(false)
  const [sessionError, setSessionError] = useState<string | null>(null)
  // Set when a residual tip was below the chargeable card minimum and the backend
  // wrote it off instead of creating a session (response.writtenOff === true).
  const [tipSettled, setTipSettled] = useState(false)
  const hasRequestedSessionRef = useRef(false)

  useEffect(() => {
    if (token) {
      setToken(id, token)
      navigate({
        to: '/booking/$id/checkout',
        params: { id },
        search: { invoice_id: invoiceId, schedule_id: scheduleId, tip_only: tipOnly },
        replace: true,
      })
      return
    }
  }, [id, invoiceId, navigate, scheduleId, tipOnly, token])

  const selectedSchedule = useMemo(() => {
    return invoice?.paymentSchedules?.find(schedule => schedule.id === scheduleId) ?? null
  }, [invoice?.paymentSchedules, scheduleId])

  const amountDueCents = useMemo(() => {
    // Tip-only mode: there is no schedule; the amount due is the outstanding tip.
    if (tipOnly) return invoice?.tipDueCents ?? 0n
    if (!selectedSchedule) return 0n
    return getScheduleAmountDue(selectedSchedule)
  }, [invoice?.tipDueCents, selectedSchedule, tipOnly])

  // Once the checkout session exists, prefer the backend's authoritative amount
  // (base + tip) so the displayed "amount due now" provably matches what Stripe
  // will charge. Before the session loads, fall back to the client-derived value.
  const displayAmountCents = checkout?.amountCents ?? amountDueCents

  // A tip-only payment follows a completed base payment, so the questionnaire gate never applies.
  const isQuestionnaireGated = !tipOnly && questionnaireGatesPayment(booking)

  useEffect(() => {
    if (!hasBookingLoaded || !isQuestionnaireGated) return
    navigate({
      to: '/booking/$id/questionnaire',
      params: { id },
      search: { invoice_id: invoiceId, schedule_id: scheduleId },
      replace: true,
    })
  }, [hasBookingLoaded, id, invoiceId, isQuestionnaireGated, navigate, scheduleId])

  useEffect(() => {
    // Base payment requires a schedule_id; the tip-only (residual) flow does not.
    if (!invoiceId || hasRequestedSessionRef.current) return
    if (!tipOnly && !scheduleId) return
    // Wait for the booking so a questionnaire-gated payment never opens a Stripe session.
    if (!hasBookingLoaded || isQuestionnaireGated) return

    hasRequestedSessionRef.current = true
    setIsCreatingSession(true)
    setSessionError(null)
    trackPaymentEvent('started', { invoiceId, scheduleId, tipOnly })

    const sessionPromise = tipOnly
      ? createResidualTipCheckoutSession(invoiceId).then(result => {
          // Sub-minimum residual tip: backend wrote it off, no session to mount.
          if (result.writtenOff) {
            setTipSettled(true)
            return null
          }
          return {
            sessionId: result.sessionId,
            clientSecret: result.clientSecret,
            stripeAccountId: result.stripeAccountId,
            expiresAt: result.expiresAt ? timestampToDate(result.expiresAt) : null,
            amountCents: result.amountCents,
          }
        })
      : createCheckoutSession(invoiceId, scheduleId!).then(result => {
          // Persist context so the post-payment processing page can restore the
          // base-schedule identity. Tip-only mode skips this (it has no schedule).
          savePaymentSessionContext({
            bookingId: id,
            invoiceId,
            scheduleId: scheduleId!,
            sessionId: result.sessionId,
            createdAt: Date.now(),
          })
          return {
            sessionId: result.sessionId,
            clientSecret: result.clientSecret,
            stripeAccountId: result.stripeAccountId,
            expiresAt: result.expiresAt ? timestampToDate(result.expiresAt) : null,
            amountCents: result.amountCents,
          }
        })

    sessionPromise
      .then(next => {
        if (next) setCheckout(next)
      })
      .catch(error => {
        console.error('Failed to create checkout session:', error)
        trackPaymentEvent('failed', { invoiceId, scheduleId, tipOnly, error: String(error) })
        captureError(error instanceof Error ? error : new Error(String(error)), {
          context: 'booking-checkout',
          action: tipOnly ? 'create-residual-tip-checkout-session' : 'create-checkout-session',
          invoiceId,
          scheduleId,
        })

        if (error instanceof ConnectError && error.code === Code.FailedPrecondition) {
          setSessionError(error.message)
        } else {
          setSessionError(getErrorDescription(error) || 'We could not start checkout. Please try again.')
        }
      })
      .finally(() => {
        setIsCreatingSession(false)
      })
  }, [createCheckoutSession, createResidualTipCheckoutSession, hasBookingLoaded, id, invoiceId, isQuestionnaireGated, scheduleId, tipOnly])

  const handleBackToBooking = useCallback(() => {
    navigate({
      to: '/booking/$id',
      params: { id },
      search: {},
    })
  }, [id, navigate])

  const handleSessionExpired = useCallback(() => {
    setSessionError('This checkout session expired. Please return to your booking and start payment again.')
    setCheckout(null)
    toast.error('Payment session expired. Please try again.')
  }, [])

  const handleCheckoutComplete = useCallback(() => {
    // Tip-only mode has no schedule_id; base payment requires one.
    if (!checkout || !invoiceId || (!tipOnly && !scheduleId)) return
    trackPaymentEvent('succeeded', { sessionId: checkout.sessionId, invoiceId, scheduleId, tipOnly })
    navigate({
      to: '/booking/$id/processing',
      params: { id },
      search: {
        session_id: checkout.sessionId,
        invoice_id: invoiceId,
        schedule_id: scheduleId,
        tip_only: tipOnly,
      },
      replace: true,
    })
  }, [checkout, id, invoiceId, navigate, scheduleId, tipOnly])

  const isLoading = isBookingLoading || !hasBookingLoaded || isInvoiceLoading || isQuestionnaireGated
  const hasMissingParams = !invoiceId || (!tipOnly && !scheduleId)

  if (isLoading) {
    return <CheckoutSkeleton />
  }

  if (isBookingError || isInvoiceError || !booking || !invoice || hasMissingParams) {
    return (
      <CheckoutShell
        orgName={booking?.organization?.name}
        logoUrl={booking?.organization?.logoUrl}
        contactEmail={booking?.organization?.contactEmail}
        onBack={handleBackToBooking}
      >
        <div className="mx-auto flex min-h-[50dvh] max-w-xl items-center justify-center px-4 sm:px-6">
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-6 text-center">
            <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-amber-600" />
            <h1 className="text-lg font-semibold text-amber-900">Checkout is unavailable</h1>
            <p className="mt-2 text-sm text-amber-800">
              We could not load the invoice payment details for this link.
            </p>
            <Button className="mt-4 min-h-11 w-full sm:min-h-9 sm:w-auto" variant="outline" onClick={handleBackToBooking}>
              Return to booking
            </Button>
          </div>
        </div>
      </CheckoutShell>
    )
  }

  return (
    <CheckoutShell
      orgName={booking.organization?.name}
      logoUrl={booking.organization?.logoUrl}
      contactEmail={booking.organization?.contactEmail}
      hasGallery={!!booking.organization?.galleryImages?.length}
      onBack={handleBackToBooking}
    >
      <main className="mx-auto grid w-full max-w-7xl gap-4 px-4 py-4 sm:gap-6 sm:py-6 md:px-8 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)] lg:py-8">
        {/* Desktop: summary rail on the left. On phones the same content moves into a
            collapsed disclosure above the form so the payment form is on screen first. */}
        <aside className="hidden space-y-4 lg:sticky lg:top-24 lg:block lg:h-fit">
          <CheckoutSummary
            invoice={invoice}
            amountDueCents={displayAmountCents}
            schedule={selectedSchedule}
            tipOnly={!!tipOnly}
            events={booking.events || []}
          />
          <SecureCheckoutBadge />
        </aside>

        <section className="min-w-0 space-y-4 sm:space-y-6">
          <div className="flex items-center gap-3">
            <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary sm:flex">
              <CreditCard className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 className="display-heading text-xl font-semibold tracking-tight sm:text-2xl">Checkout</h1>
              <p className="text-sm text-muted-foreground">
                {tipOnly
                  ? 'Complete your remaining tip payment.'
                  : `Complete your payment for ${selectedSchedule?.description || 'this invoice'}.`}
              </p>
            </div>
          </div>

          <MobileOrderSummary amountDueCents={displayAmountCents}>
            <CheckoutSummary
              invoice={invoice}
              amountDueCents={displayAmountCents}
              schedule={selectedSchedule}
              tipOnly={!!tipOnly}
              events={booking.events || []}
            />
          </MobileOrderSummary>

          {/* Full-bleed on phones: Stripe's form brings its own padding, so nesting it
              inside the page gutter and card padding wastes ~a fifth of the screen. */}
          <div className="-mx-4 border-y bg-card py-4 sm:mx-0 sm:rounded-lg sm:border sm:p-4 md:p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b px-4 pb-4 sm:px-0">
              <div>
                <p className="text-sm text-muted-foreground">Amount due now</p>
                <p className="font-financial text-2xl font-semibold">{formatCents(Number(displayAmountCents))}</p>
              </div>
              {checkout?.expiresAt && (
                <SessionCountdown expiresAt={checkout.expiresAt} onExpired={handleSessionExpired} />
              )}
            </div>

            {sessionError ? (
              <div className="mx-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive sm:mx-0">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{sessionError}</span>
                </div>
                <Button className="mt-4 min-h-11 w-full sm:min-h-9 sm:w-auto" variant="outline" onClick={handleBackToBooking}>
                  Return to booking
                </Button>
              </div>
            ) : tipSettled ? (
              <div className="mx-4 rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-800 sm:mx-0">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>This tip has been settled — no payment is needed.</span>
                </div>
                <Button className="mt-4 min-h-11 w-full sm:min-h-9 sm:w-auto" variant="outline" onClick={handleBackToBooking}>
                  Return to booking
                </Button>
              </div>
            ) : isCreatingSession || !checkout ? (
              <div className="flex min-h-[280px] items-center justify-center">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Preparing secure checkout...
                </div>
              </div>
            ) : (
              <ErrorBoundary>
                <Suspense fallback={<Skeleton className="h-72 w-full" />}>
                  <EmbeddedCheckoutForm
                    clientSecret={checkout.clientSecret}
                    stripeAccountId={checkout.stripeAccountId}
                    onComplete={handleCheckoutComplete}
                  />
                </Suspense>
              </ErrorBoundary>
            )}

            {/* Saved payment method disclosure (Stripe SetupFutureUsage=off_session) */}
            {checkout && !sessionError && !tipSettled && (
              <p className="mt-4 px-4 text-xs text-muted-foreground sm:px-0">
                Your payment method may be securely saved for future payments on this booking.
              </p>
            )}
          </div>

          <div className="lg:hidden">
            <SecureCheckoutBadge />
          </div>
        </section>
      </main>

      {booking.organization && <PortalFooter organization={booking.organization} />}
    </CheckoutShell>
  )
}

function CheckoutShell({
  orgName,
  logoUrl,
  contactEmail,
  hasGallery,
  onBack,
  children,
}: {
  orgName?: string
  logoUrl?: string
  contactEmail?: string
  hasGallery?: boolean
  onBack: () => void
  children: React.ReactNode
}) {
  return (
    <div className="min-h-svh bg-background">
      <PortalHeader
        orgName={orgName || 'Booking checkout'}
        logoUrl={logoUrl}
        contactEmail={contactEmail}
        hasGallery={hasGallery}
        backLabel="Return to booking"
        onBack={onBack}
      />
      {children}
    </div>
  )
}

function SecureCheckoutBadge() {
  return (
    <div className="flex items-center gap-2 rounded-md bg-foreground px-4 py-3 text-background">
      <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-400" />
      <span className="text-xs font-medium">Secure checkout powered by Stripe</span>
    </div>
  )
}

/**
 * Phone-only collapsed order summary (the familiar "Show order summary · $X" bar).
 * Collapsed by default so the payment form is visible without scrolling; the
 * full breakdown is one tap away.
 */
function MobileOrderSummary({
  amountDueCents,
  children,
}: {
  amountDueCents: bigint
  children: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useState(false)
  const panelId = useId()

  return (
    <div className="lg:hidden">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen(open => !open)}
        className="flex min-h-12 w-full items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3 text-left text-sm"
      >
        <span className="flex items-center gap-2 font-medium">
          <Receipt className="h-4 w-4 text-muted-foreground" />
          {isOpen ? 'Hide order summary' : 'Show order summary'}
          <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', isOpen && 'rotate-180')} />
        </span>
        <span className="font-financial font-semibold">{formatCents(Number(amountDueCents))}</span>
      </button>
      <div id={panelId} hidden={!isOpen} className="mt-3 space-y-3">
        {children}
      </div>
    </div>
  )
}

function CheckoutSummary({
  invoice,
  amountDueCents,
  schedule,
  tipOnly,
  events,
}: {
  invoice: PublicInvoice
  amountDueCents: bigint
  schedule: NonNullable<PublicInvoice['paymentSchedules']>[number] | null
  tipOnly: boolean
  events: Array<{
    id: string
    eventDate?: { seconds?: bigint | number }
    eventLength?: number
    eventGuests?: number
    eventLocation?: string
    eventTimezone?: string
  }>
}) {
  const paymentTotals = getCustomerFacingInvoicePaymentTotals(invoice)

  return (
    <>
      <div className="rounded-lg border bg-card p-4">
        <div className="mb-4 flex items-center gap-2">
          <Receipt className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold">Order summary</h2>
        </div>
        <div className="space-y-2 text-sm">
          <SummaryRow label="Subtotal" value={formatCents(Number(invoice.subtotalCents))} />
          {invoice.discountCents > 0n && (
            <SummaryRow label={formatDiscountLabel(invoice)} value={`-${formatCents(Number(invoice.discountCents))}`} tone="success" />
          )}
          {invoice.taxCents > 0n && (
            <SummaryRow label="Sales tax" value={formatCents(Number(invoice.taxCents))} />
          )}
          {invoice.tipCents > 0n && (
            <SummaryRow label="Tip" value={formatCents(Number(invoice.tipCents))} />
          )}
          {paymentTotals.paidCents > 0n && (
            <>
              <SummaryRow label="Paid" value={`-${formatCents(Number(paymentTotals.paidCents))}`} tone="success" />
              {paymentTotals.tipPaidCents > 0n && <TipPaidNote tipPaidCents={paymentTotals.tipPaidCents} />}
            </>
          )}
          <div className="mt-3 border-t pt-3">
            <SummaryRow label="Due now" value={formatCents(Number(amountDueCents))} strong />
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold">Payment</h2>
        <div className="flex items-start justify-between gap-3 text-sm">
          <div className="min-w-0">
            <p className="font-medium">{tipOnly ? 'Remaining tip' : schedule?.description || 'Invoice payment'}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {tipOnly
                ? 'Gratuity'
                : schedule?.dueDate
                  ? `Due ${formatDateOnlyUTC(schedule.dueDate, 'MMM d, yyyy')}`
                  : 'No due date'}
            </p>
          </div>
          <p className="shrink-0 font-semibold font-financial">{formatCents(Number(amountDueCents))}</p>
        </div>
      </div>

      {events.length > 0 && (
        <div className="rounded-lg border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold">Event details</h2>
          <div className="space-y-4">
            {events.map((event, index) => (
              <div key={event.id || index} className="space-y-2 border-b pb-4 last:border-0 last:pb-0">
                {events.length > 1 && (
                  <p className="text-xs font-medium uppercase text-muted-foreground">Event {index + 1}</p>
                )}
                {event.eventDate && (
                  <EventLine icon={<Calendar className="h-4 w-4" />}>
                    {formatDateTimeRange(event.eventDate, event.eventLength, event.eventTimezone)}
                  </EventLine>
                )}
                {event.eventLength !== undefined && event.eventLength > 0 && (
                  <EventLine icon={<Clock className="h-4 w-4" />}>
                    {event.eventLength} hour{event.eventLength === 1 ? '' : 's'}
                  </EventLine>
                )}
                {event.eventLocation && (
                  <EventLine icon={<MapPin className="h-4 w-4" />}>{event.eventLocation}</EventLine>
                )}
                {event.eventGuests !== undefined && event.eventGuests > 0 && (
                  <EventLine icon={<Users className="h-4 w-4" />}>
                    {event.eventGuests} guest{event.eventGuests === 1 ? '' : 's'}
                  </EventLine>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

function TipPaidNote({ tipPaidCents }: { tipPaidCents: bigint }) {
  return (
    <div className="-mt-1 text-right text-xs text-muted-foreground font-financial">
      incl. {formatCents(Number(tipPaidCents))} tip
    </div>
  )
}

function SummaryRow({
  label,
  value,
  strong = false,
  tone,
}: {
  label: string
  value: string
  strong?: boolean
  tone?: 'success'
}) {
  return (
    <div className={cn('flex justify-between gap-3', strong && 'text-base font-semibold')}>
      <span className={cn('min-w-0', tone === 'success' ? 'text-emerald-700' : 'text-muted-foreground')}>{label}</span>
      <span className="shrink-0 font-financial">{value}</span>
    </div>
  )
}

function EventLine({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-sm text-muted-foreground">
      <span className="mt-0.5 shrink-0 text-muted-foreground">{icon}</span>
      <span className="min-w-0 break-words">{children}</span>
    </div>
  )
}

function CheckoutSkeleton() {
  return (
    <div className="min-h-svh">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:py-4 md:px-8">
          <Skeleton className="h-10 w-40 sm:w-52" />
          <Skeleton className="h-9 w-9 sm:w-36" />
        </div>
      </header>
      <main className="mx-auto grid max-w-7xl gap-4 px-4 py-4 sm:gap-6 sm:py-8 md:px-8 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)]">
        <div className="hidden space-y-4 lg:block">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-12 w-full lg:hidden" />
          <Skeleton className="h-[520px] w-full" />
        </div>
      </main>
    </div>
  )
}

function getScheduleAmountDue(schedule: NonNullable<PublicInvoice['paymentSchedules']>[number]) {
  const goodwillRefundedCents = schedule.goodwillRefundedCents || 0n
  return (schedule.amountWithTipCents ?? schedule.amountCents) - goodwillRefundedCents - schedule.amountPaidCents
}
